import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { UserProfileDropdown, type NotificationItem } from "@/components/common/UserProfileDropdown";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { ADMIN_ROUTES } from "@/routes";

type SuperadminNavKey = "templates" | "management" | "events" | "campaigns";

interface AdminHeaderProps {
  currentLocale: string;
  active: SuperadminNavKey;
  planCode?: string;
  planName?: string;
}

/**
 * Administrator Header - Only for administrator routes
 * Regular users should use UserHeader instead
 */
export function AdminHeader({
  currentLocale,
  active,
}: AdminHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const navItems: { key: SuperadminNavKey; label: string; to: string }[] = [
    {
      key: "templates",
      label: t("navigation.templates"),
      to: ADMIN_ROUTES.templates(currentLocale),
    },
    {
      key: "events",
      label: t("navigation.events"),
      to: ADMIN_ROUTES.events(currentLocale),
    },
    {
      key: "campaigns",
      label: t("navigation.campaigns"),
      to: ADMIN_ROUTES.campaigns(currentLocale),
    },
    {
      key: "management",
      label: t("navigation.management"),
      to: ADMIN_ROUTES.management(currentLocale),
    },
  ];

  // Admin users don't have plan notifications
  const notifications: NotificationItem[] = [];

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate(ADMIN_ROUTES.templates(currentLocale))}>
            <h1 className="font-logo text-3xl font-bold bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">
              {t("header.logo")}
            </h1>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Button
                key={item.key}
                variant={item.key === active ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "text-sm px-4",
                  item.key === active
                    ? "bg-pink-50 text-pink-700 dark:bg-pink-950/30 dark:text-pink-200"
                    : "text-muted-foreground hover:text-foreground"
                )}
                onClick={() => navigate(item.to)}
              >
                {item.label}
              </Button>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <LanguageSwitcher compact />
          {user && (
            <UserProfileDropdown
              userName={user.user_name}
              isAdmin={true}
              notifications={notifications}
            />
          )}
        </div>

        {/* Below md the main nav is hidden and the avatar menu has no admin links: a scrollable row keeps the sections reachable (JUB-203) */}
        <nav className="flex w-full items-center gap-1 overflow-x-auto md:hidden" aria-label="management">
          {navItems.map((item) => (
            <Button
              key={item.key}
              variant={item.key === active ? "default" : "ghost"}
              size="sm"
              className={cn(
                "shrink-0 text-sm px-3",
                item.key === active
                  ? "bg-pink-50 text-pink-700 dark:bg-pink-950/30 dark:text-pink-200"
                  : "text-muted-foreground hover:text-foreground"
              )}
              onClick={() => navigate(item.to)}
            >
              {item.label}
            </Button>
          ))}
        </nav>
      </div>
    </header>
  );
}

